<script setup lang="ts">
import { computed, onMounted, onUnmounted, watchEffect } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { generatedRoutes } from '@/config/router'
import {
  SidebarNav,
  ConfigPanel,
  DebugPanel,
  ElementsPanel,
  TimelinePanel,
  SoundPanel,
  SoundEditorOverlay,
  PanelContainer
} from '@/components/panels'
import GlobalNavigation from '@/components/GlobalNavigation.vue'
import { usePanelsStore } from '@/stores/panels'
import { useSceneConfigStore } from '@/stores/sceneConfig'

const router = useRouter()
const route = useRoute()

const panelsStore = usePanelsStore()
panelsStore.initRouteSync()

const sceneConfigStore = useSceneConfigStore()
watchEffect(() => {
  document.body.style.userSelect = sceneConfigStore.getValue('global.textSelection') ? '' : 'none'
})

onMounted(() => {
  window.addEventListener('keydown', handleKeyPress)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress)
})

const handleKeyPress = (event: KeyboardEvent) => {
  const { name, query } = router.currentRoute.value
  const page = typeof name === 'string' ? generatedRoutes.map((r) => r?.name).indexOf(name) : 0

  switch (event.key) {
    case 'PageDown': {
      const path = generatedRoutes[page + 1]?.path
      if (page < generatedRoutes.length - 1) router.push({ query, path })
      break
    }

    case 'PageUp': {
      const path = generatedRoutes[page - 1]?.path
      if (page > 0) router.push({ query, path })
      break
    }

    case 'Home': {
      toggleQuery('record')
      break
    }

    case 'End': {
      toggleQuery(['control', 'stats'])
      break
    }

    default:
      break
  }
}

const toggleQuery = (parameter: string | string[]) => {
  const { path, query } = router.currentRoute.value
  const params = typeof parameter === 'string' ? [parameter] : parameter
  const newQuery = params.reduce(
    (accumulator, key) => ({ ...accumulator, [key]: query[key] === 'true' ? undefined : 'true' }),
    {}
  )
  router.push({ path, query: { ...query, ...newQuery } })
}

const isRecording = computed(() => !!route.query.record)

const handleStartRecording = (durationMs: number) => {
  const fps = 30
  const totalFrames = Math.floor((durationMs / 1000) * fps)
  router.push({ query: { ...route.query, record: String(totalFrames) } })
}

const handleStopRecording = () => {
  const query = { ...route.query }
  delete query.record
  router.push({ query })
}
</script>

<template>
  <RouterView />
  <GlobalNavigation />
  <PanelContainer side="left" />
  <PanelContainer side="right" />
  <SidebarNav />
  <ConfigPanel />
  <DebugPanel />
  <TimelinePanel />
  <SoundPanel />
  <SoundEditorOverlay />
  <ElementsPanel
    :is-recording="isRecording"
    @start="handleStartRecording"
    @stop="handleStopRecording"
  />
</template>

<style>
body {
  background-color: var(--color-background);
}

canvas {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition: top 0.2s;
}
</style>
